import { differenceInDays } from 'date-fns';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    trialFromQueryReplacementForPaidUser,
    trialFromQueryReplacementForPaidUserDays,
    trialFromQueryReplacementForPaidUserTitle,
} from 'reactApp/appHelpers/featuresHelpers/features/trialFromQueryReplacementForPaidUser';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import { getPastSubscriptionsIfNotLoaded } from 'reactApp/modules/pastSubscriptions/pastSubscriptions.module';
import { PastSubscriptionsSelectors } from 'reactApp/modules/pastSubscriptions/pastSubscriptions.selectors';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';

export function resolveTrialReplacementForPaidUser({
    productIdFromQuery,
    isPaidUser,
    latestSubs,
}: {
    isPaidUser: boolean;
    productIdFromQuery: string;
    latestSubs?: BillingDataSubscriptionInfo;
}) {
    const daysPassedSinceSubsEnded = latestSubs ? differenceInDays(new Date(), latestSubs.expires) : Infinity;
    const wasSubsInThePast = daysPassedSinceSubsEnded < trialFromQueryReplacementForPaidUserDays;
    const replacementId = trialFromQueryReplacementForPaidUser[productIdFromQuery] || productIdFromQuery;
    const disableTrial = trialFromQueryReplacementForPaidUser[productIdFromQuery] && (isPaidUser || wasSubsInThePast);
    const resolvedProductId = disableTrial ? replacementId : productIdFromQuery;
    const resolvedTitle = disableTrial ? trialFromQueryReplacementForPaidUserTitle : undefined;
    return { resolvedProductId, resolvedTitle };
}

export function useTrialFromQueryReplacementForPaidUser({ productIdFromQuery }: { productIdFromQuery: string }) {
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(getPastSubscriptionsIfNotLoaded());
    }, [dispatch]);

    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const latestSubs = useSelector(PastSubscriptionsSelectors.latestSubsByExpires);
    const { resolvedProductId, resolvedTitle } = resolveTrialReplacementForPaidUser({ productIdFromQuery, isPaidUser, latestSubs });
    const productFromQuery = useSelector((state: RootState) => ProductsSelectors.getProductById(state, resolvedProductId));
    return { productFromQuery, title: resolvedTitle };
}
